/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */
import { SportConfigToken } from '@config/sport.config';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { Player, PlayerExtend } from '@interfaces/match-data.interface';
import { PageService } from '@services/page.service';
import { BaseComponent } from '@components/base.component';
import { snakeCase } from 'lodash-es';

@Component({
    selector: 'app-player-match-stats',
    templateUrl: './player-match-stats.component.html',
})
export class PlayerMatchStatsComponent extends BaseComponent implements OnInit {
    @Input()
    public sportName: string;

    @Input()
    public sportId: number;

    @Input()
    public players: Player[];

    public player: PlayerExtend | null = null;

    public visible: boolean = false;

    private statsOrder: Record<string, number> = {


        GoalsScored: -1,
        Goals: -1,
        Points: -1,
        Assists: -1,
        FirstAssists: -1,
        SecondAssists: -1,
        MinutesPlayed: -1,
        GoalsByHead: -1,
        GoalsByPenalty: -1,
        GoalsConceded: -1,
        OwnGoals: -1,
        FieldGoals: -1,
        SevenMGoals: -1,

        passing_air_yards: -1,

        passing_attempts: -1,

        passing_avg_pocket_time: -1,

        passing_avg_yards: -1,

        passing_batted_passes: -1,

        passing_blitzes: -1,

        passing_cmp_pct: -1,

        passing_completions: -1,

        passing_defended_passes: -1,

        passing_dropped_passes: -1,

        passing_first_downs: -1,

        passing_hurries: -1,

        passing_int_touchdowns: -1,

        passing_interceptions: -1,

        passing_knockdowns: -1,

        passing_longest: -1,

        passing_longest_touchdown: -1,

        passing_on_target_throws: -1,

        passing_pocket_time: -1,

        passing_poor_throws: -1,

        passing_rating: -1,

        passing_redzone_attempts: -1,

        passing_sack_yards: -1,

        passing_sacks: -1,

        passing_spikes: -1,

        passing_throw_aways: -1,

        passing_touchdowns: -1,

        passing_yards: -1,

        Line1: -2,

        Offsides: -1,
        PassesTotal: -1,
        PassesSuccessful: -1,
        PassesUnsuccessful: -1,
        CrossesTotal: -1,
        CrossesSuccessful: -1,
        LongPassesTotal: -1,
        LongPassesSuccessful: -1,
        LongPassesUnsuccessful: -1,
        ChancesCreated: -1,
        Line2: -2,
        ShotsOnTarget: -1,
        Shots: -1,
        ShotsOnGoal: -1,
        ShotsOffGoal: -1,
        ShotsOffTarget: -1,
        ShotsBlocked: -1,
        ShotsFacedTotal: -1,
        ShotsFacedSaved: -1,
        PlusMinus: -1,
        DribblesCompleted: -1,

        rushing_attempts: -1,

        rushing_avg_yards: -1,

        rushing_broken_tackles: -1,

        rushing_first_downs: -1,

        rushing_kneel_downs: -1,

        rushing_longest: -1,

        rushing_longest_touchdown: -1,

        rushing_redzone_attempts: -1,

        rushing_scrambles: -1,

        rushing_tlost: -1,

        rushing_tlost_yards: -1,

        rushing_touchdowns: -1,

        rushing_yards: -1,

        rushing_yards_after_contact: -1,


        Line3: -2,

        YellowCards: -1,
        RedCards: -1,
        YellowRedCards: -1,
        FoulsCommitted: -1,
        WasFouled: -1,
        total_yards: -1,

        total_avg_yards: -1,

        total_touchdowns: -1,


        Line4: -2,

        Clearances: -1,
        TacklesSuccessful: -1,
        TacklesTotal: -1,
        CornerKicks: -1,
        DefensiveBlocks: -1,
        Interceptions: -1,

        Line5: -2,

        DivingSaves: -1,
        LossOfPossession: -1,
        PenaltiesFaced: -1,
        PenaltiesMissed: -1,
        PenaltiesSaved: -1,
        SubstitutedIn: -1,
        SubstitutedOut: -1,
    };

    private roundStats = [
        'defensive_rating',
        'offensive_rating',
        'defensive_rebounds_pct',
        'offensive_rebounds_pct',
        'rebounds_pct',
        'steals_pct',
        'turnovers_pct',
    ];

    constructor(@Inject(SportConfigToken) private sportConfig: Record<string, any>, public page: PageService) {
        super();
    }

    public ngOnInit(): void {
        this.page.$onPlayerMatch.subscribe((player: PlayerExtend) => {
            this.close();
            this.player = null;
            this.player = player;

            this.visible = true;
        });
    }

    get stats(): [string, unknown][] {
        const merged = this.player?.statistics ? { ...this.statsOrder, ...this.player?.statistics } : [];
        let stats: [string, number][] = merged ? Object.entries(merged) : [];


        stats = stats.filter((v: [string, number]) => v[1] !== -1);

        stats = stats.filter((v: [string, number], key) => {
            if (stats[key - 1] && stats[key - 1]![1] === -2 && v[1] === -2) {
                return false;
            }

            return true;
        });

        return stats;
    }

    public snakeCase(val: string): string {
        return snakeCase(val);
    }

    get hasStats(): boolean {
        return this.stats && this.stats.length > 0;
    }

    get name(): string {
        return this.player?.name_short ?? this.player?.name ?? '';
    }

    public close(): void {
        this.visible = false;
        // this.player = null;
    }

    public getTooltipTranslationKey(statName: string): string {
        let leagueKey = '';
        if (this.player?.betradar_major_league) {
            switch (this.sportName) {
                case 'basketball':
                    leagueKey = 'nba_';
                    break;
                case 'ice-hockey':
                    leagueKey = 'nhl_';
                    break;
                default: break;
            }
        }

        return `web.playerstats_tooltip_${leagueKey}${this.snakeCase(statName)}`;
    }

    private getNormalizedStatValue(statName: string, value: string | null | undefined): string {
        if (!value) {
            return '-';
        }

        // eslint-disable-next-line no-restricted-globals
        if (value && isNaN(Number(value))) {
            if (value === '00:00') {
                return '-';
            }

            return value;
        }

        if (value && this.roundStats.includes(statName)) {
            value = Number(value).toFixed(3);
        }

        const postfix = statName.endsWith('_pct') ? ' %' : '';

        return +value
            ? value + postfix
            : '-';
    }
}
